<template>
  <div class="documents-panel">

    <!-- ── Filter toggle bar ── -->
    <div class="filter-toggle-bar">
      <button class="filter-toggle-btn" type="button" @click="showFilters = !showFilters">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
        Filtros
        <span v-if="activeFilterCount > 0" class="filter-active-badge">{{ activeFilterCount }}</span>
        <svg class="filter-chevron" :class="{ open: showFilters }" width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 4l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
      <button v-if="activeFilterCount > 0" class="filter-clear-btn" type="button" @click="clearFilters">Limpar filtros</button>
    </div>

    <!-- ── Filters ── -->
    <Transition name="filter-bar">
      <div v-show="showFilters" class="filter-bar">
        <div class="filter-group">
          <label class="filter-label">Tipo</label>
          <select class="filter-select" v-model="typeFilter">
            <option value="">Todos os tipos</option>
            <option v-for="item in typeOptions" :key="item" :value="item">{{ item }}</option>
          </select>
        </div>
        <div class="filter-group">
          <label class="filter-label">Categoria</label>
          <select class="filter-select" v-model="categoryFilter">
            <option value="">Todas</option>
            <option value="relatorio">Relatório</option>
            <option value="dialogo">Diálogo</option>
            <option value="ocorrido">Ocorrido</option>
            <option value="permissao">Permissão</option>
          </select>
        </div>
        <div class="filter-group">
          <label class="filter-label">Status</label>
          <select class="filter-select" v-model="statusFilter">
            <option value="">Todos</option>
            <option value="enviado">Enviado</option>
            <option value="rascunho">Rascunho</option>
          </select>
        </div>
        <div class="filter-group">
          <label class="filter-label">Autor</label>
          <select class="filter-select" v-model="authorFilter">
            <option value="">Todos os autores</option>
            <option v-for="item in authorOptions" :key="item" :value="item">{{ item }}</option>
          </select>
        </div>
        <div class="filter-group">
          <label class="filter-label">De</label>
          <input class="filter-input" v-model="dateFrom" type="date" />
        </div>
        <div class="filter-group">
          <label class="filter-label">Até</label>
          <input class="filter-input" v-model="dateTo" type="date" />
        </div>
      </div>
    </Transition>

    <!-- ── Selection toolbar ── -->
    <Transition name="sel-bar">
      <div v-if="selectedDocs.length > 0" class="selection-bar">
        <span class="sel-count">{{ selectedDocs.length }} selecionado{{ selectedDocs.length > 1 ? 's' : '' }}</span>
        <div class="sel-actions">
          <button class="export-btn export-btn--doc" type="button" @click="openLang('doc')">
            <svg width="14" height="14" viewBox="0 0 15 15" fill="none"><rect x="2" y="1" width="11" height="13" rx="1.5" stroke="currentColor" stroke-width="1.3"/><path d="M5 5h5M5 7.5h5M5 10h3" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
            Word
          </button>
          <button class="export-btn export-btn--pdf" type="button" @click="openLang('pdf')">
            <svg width="14" height="14" viewBox="0 0 15 15" fill="none"><path d="M3 1.5h6l3 3V13a.5.5 0 01-.5.5H3A.5.5 0 012.5 13V2A.5.5 0 013 1.5z" stroke="currentColor" stroke-width="1.3"/><path d="M9 1.5V4.5h3M5 7h1.5c.828 0 1.5.448 1.5 1s-.672 1-1.5 1H5V10" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            PDF
          </button>
          <button v-if="accessMode === 'edit'" class="export-btn export-btn--delete" type="button" @click="askDelete">
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M2 3.5h10M5.5 3.5V2.5h3V3.5M4.5 3.5v7.5h5V3.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>
            Apagar
          </button>
          <button class="export-btn export-btn--cancel" type="button" @click="selectedDocs = []">
            Cancelar
          </button>
        </div>
      </div>
    </Transition>

    <!-- ── Empty ── -->
    <SkeletonList v-if="loading" variant="row" :count="5" :columns="[3, 40, 15, 10, 15]" />

    <div v-else-if="filteredItems.length === 0" class="empty-state">
      <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
      <p class="empty-title">Nenhum relatório encontrado</p>
      <p class="empty-sub">Tente ajustar os filtros acima.</p>
    </div>

    <!-- ── List ── -->
    <div v-else class="doc-list">
      <div class="doc-list-header">
        <div class="col-check"></div>
        <div class="col-title">Documento</div>
        <div class="col-date">Data</div>
        <div class="col-status">Status</div>
        <div class="col-author">Autor</div>
        <div class="col-actions"></div>
      </div>

      <div
        v-for="item in filteredItems"
        :key="item.id"
        class="doc-row"
        :class="{ selected: selectedDocs.includes(item.id) }"
      >
        <div class="col-check">
          <input
            class="doc-checkbox"
            type="checkbox"
            :checked="selectedDocs.includes(item.id)"
            @change="toggleDoc(item.id)"
          />
        </div>
        <div class="col-title">
          <span class="type-badge" :data-cat="item.category">{{ categoryLabel(item.category) }}</span>
          <div class="doc-title-wrap">
            <button class="doc-title doc-title-link" type="button" @click="$emit('view-report', item.id)">{{ item.title }}</button>
            <span class="doc-date-mobile">{{ formatDateOnly(item.reportDate) }}</span>
            <span v-if="activitiesText(item)" class="doc-preview">{{ activitiesText(item) }}</span>
          </div>
        </div>
        <div class="col-date">{{ formatDateOnly(item.reportDate) }}</div>
        <div class="col-status">
          <span class="status-pill" :class="`status-pill--${item.status}`">
            {{ item.status === 'enviado' ? 'Enviado' : 'Rascunho' }}
          </span>
        </div>
        <div class="col-author">
          <div class="author-avatar" :title="item.author.name">{{ item.author.name?.charAt(0)?.toUpperCase() }}</div>
          <span class="author-name">{{ item.author.name }}</span>
        </div>
        <div class="col-actions">
          <button class="row-link" type="button" @click="$emit('view-report', item.id)">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            Ver
          </button>
          <button class="row-link" type="button" @click="$emit('edit-report', item.id)">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            Editar
          </button>
        </div>
      </div>
    </div>

    <!-- ── Confirm delete modal ── -->
    <Teleport to="body">
      <div v-if="showConfirmDelete" class="lang-overlay" @click.self="showConfirmDelete = false">
        <div class="lang-modal lang-modal--sm">
          <div class="lang-modal-header">
            <h3 class="lang-modal-title">Apagar relatórios</h3>
            <p class="lang-modal-sub">Deseja apagar {{ selectedDocs.length }} relatório{{ selectedDocs.length > 1 ? 's' : '' }} permanentemente?</p>
          </div>
          <div class="lang-modal-footer lang-modal-footer--gap">
            <button class="lang-cancel" type="button" @click="showConfirmDelete = false">Cancelar</button>
            <button class="delete-confirm-btn" type="button" @click="confirmDelete">Apagar</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ── Language Modal ── -->
    <Teleport to="body">
      <div v-if="showLangModal" class="lang-overlay" @click.self="showLangModal = false">
        <div class="lang-modal">
          <div class="lang-modal-header">
            <h3 class="lang-modal-title">
              {{ exportTarget === 'doc' ? 'Gerar documento Word (.docx)' : 'Gerar PDF' }}
            </h3>
            <p class="lang-modal-sub">Selecione o idioma do documento gerado</p>
          </div>
          <div class="lang-grid">
            <button v-for="loc in LANGUAGES" :key="loc.code" class="lang-option" type="button" @click="selectLang(loc.code)">
              <span class="lang-flag">{{ loc.flag }}</span>
              <span class="lang-label">{{ loc.label }}</span>
            </button>
          </div>
          <div class="lang-modal-footer">
            <button class="lang-cancel" type="button" @click="showLangModal = false">Cancelar</button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, type PropType } from 'vue'
import type { Submission } from '@/store/submissions'
import { useReportExport } from '@/composables/useReportExport'
import { submissionEditState } from '@/store/submissionEditState'
import { formatDateOnly } from '@/utils/date'
import { systemStore } from '@/store/system'
import SkeletonList from '@/components/skeleton/SkeletonList.vue'

const LANGUAGES = [
  { code: 'pt', label: 'Português',  flag: '🇧🇷' },
  { code: 'en', label: 'English',    flag: '🇺🇸' },
  { code: 'es', label: 'Español',    flag: '🇪🇸' },
  { code: 'de', label: 'Deutsch',    flag: '🇩🇪' },
  { code: 'fr', label: 'Français',   flag: '🇫🇷' },
  { code: 'it', label: 'Italiano',   flag: '🇮🇹' },
  { code: 'zh', label: '中文',        flag: '🇨🇳' },
  { code: 'ja', label: '日本語',      flag: '🇯🇵' },
  { code: 'ko', label: '한국어',      flag: '🇰🇷' },
  { code: 'ru', label: 'Русский',    flag: '🇷🇺' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
]

const CATEGORY_LABELS: Record<string, string> = {
  relatorio: 'Relatório',
  dialogo: 'Diálogo',
  ocorrido: 'Ocorrido',
  permissao: 'Permissão',
}

export default defineComponent({
  name: 'ProjectReportsSection',
  components: { SkeletonList },
  props: {
    items: { type: Array as PropType<Submission[]>, default: () => [] },
    accessMode: { type: String as PropType<'read' | 'edit'>, default: 'read' },
    loading: { type: Boolean, default: false },
  },
  emits: ['view-report', 'edit-report', 'delete-reports'],
  setup(props, { emit }) {
    // ── Filters ──────────────────────────────────────────────
    const showFilters    = ref(false)
    const typeFilter     = ref('')
    const categoryFilter = ref('')
    const statusFilter   = ref('')
    const authorFilter   = ref('')
    const dateFrom       = ref('')
    const dateTo         = ref('')

    const activeFilterCount = computed(() =>
      [typeFilter.value, categoryFilter.value, statusFilter.value, authorFilter.value, dateFrom.value, dateTo.value]
        .filter(Boolean).length
    )

    const clearFilters = () => {
      typeFilter.value = ''
      categoryFilter.value = ''
      statusFilter.value = ''
      authorFilter.value = ''
      dateFrom.value = ''
      dateTo.value = ''
    }

    const typeOptions = computed(() => {
      const s = new Set<string>()
      props.items.forEach((i) => { if (i.title) s.add(i.title) })
      return Array.from(s).sort((a, b) => a.localeCompare(b))
    })

    const authorOptions = computed(() => {
      const s = new Set<string>()
      props.items.forEach((i) => { if (i.author?.name) s.add(i.author.name) })
      return Array.from(s).sort((a, b) => a.localeCompare(b))
    })

    const filteredItems = computed(() =>
      props.items.filter((item) => {
        if (typeFilter.value     && item.title          !== typeFilter.value)     return false
        if (categoryFilter.value && item.category       !== categoryFilter.value) return false
        if (statusFilter.value   && item.status         !== statusFilter.value)   return false
        if (authorFilter.value   && item.author?.name   !== authorFilter.value)   return false
        if (dateFrom.value       && item.reportDate      < dateFrom.value)        return false
        if (dateTo.value         && item.reportDate      > dateTo.value)          return false
        return true
      })
    )

    // ── Selection ─────────────────────────────────────────────
    const selectedDocs = ref<string[]>([])

    const toggleDoc = (id: string) => {
      if (selectedDocs.value.includes(id)) {
        selectedDocs.value = selectedDocs.value.filter((d) => d !== id)
      } else {
        selectedDocs.value = [...selectedDocs.value, id]
      }
    }

    // ── Export ────────────────────────────────────────────────
    const systemPrimaryColor = computed(() => systemStore.state.config.colors?.primary || '#0f4c81')
    const systemReportColor = computed(() => systemStore.state.config.colors?.report || systemPrimaryColor.value)
    const { exportSelectedDocsAsDoc, exportSelectedDocsAsPdf } = useReportExport({
      selectedDocs,
      documentsItems: computed(() => props.items),
      formatDateOnly,
      systemColor: systemReportColor,
    })

    const showLangModal  = ref(false)
    const exportTarget   = ref<'doc' | 'pdf'>('doc')

    const openLang = (target: 'doc' | 'pdf') => {
      exportTarget.value  = target
      showLangModal.value = true
    }

    const selectLang = (lang: string) => {
      showLangModal.value = false
      if (exportTarget.value === 'doc') {
        exportSelectedDocsAsDoc(lang)
      } else {
        const ids = selectedDocs.value
        exportSelectedDocsAsPdf(lang, submissionEditState.getMergedSizeFactors(ids), submissionEditState.getMergedPaired(ids))
      }
    }

    // ── Delete ────────────────────────────────────────────────
    const showConfirmDelete = ref(false)

    const askDelete = () => { showConfirmDelete.value = true }

    const confirmDelete = () => {
      const ids = [...selectedDocs.value]
      showConfirmDelete.value = false
      selectedDocs.value = []
      emit('delete-reports', ids)
    }

    // ── Helpers ───────────────────────────────────────────────
    const categoryLabel = (cat: string) => CATEGORY_LABELS[cat] ?? cat

    const activitiesText = (item: Submission) => {
      const list = item.activities?.map((a) => a.description).filter(Boolean) || []
      return list.length ? list.join(' | ') : ''
    }

    return {
      showFilters, typeFilter, categoryFilter, statusFilter, authorFilter, dateFrom, dateTo,
      activeFilterCount, clearFilters,
      typeOptions, authorOptions, filteredItems,
      selectedDocs, toggleDoc,
      showLangModal, exportTarget, openLang, selectLang, LANGUAGES,
      showConfirmDelete, askDelete, confirmDelete,
      categoryLabel, activitiesText, formatDateOnly,
    }
  }
})
</script>

<style scoped lang="scss">
/* ── Filter toggle ── */
.filter-toggle-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.filter-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--card, #fff);
  border: 1px solid rgba(31,30,26,.12);
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-muted, #888);
  cursor: pointer;
  transition: background .12s, border-color .12s;
  &:hover { background: rgba(31,30,26,.04); border-color: rgba(31,30,26,.22); color: var(--ink, #2c2b27); }
}

.filter-active-badge {
  background: var(--accent, #0078d4);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 10px;
  line-height: 1.4;
}

.filter-chevron {
  color: var(--ink-muted, #aaa);
  transition: transform .18s;
  &.open { transform: rotate(180deg); }
}

.filter-clear-btn {
  background: none;
  border: none;
  font-size: 12px;
  color: var(--accent, #0078d4);
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  &:hover { background: rgba(0,120,212,.06); }
}

/* ── Filter bar ── */
.filter-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
  background: var(--card, #fff);
  border: 1px solid rgba(31,30,26,.1);
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 4px;
}

.filter-bar-enter-active, .filter-bar-leave-active { transition: opacity .18s ease, transform .18s ease; }
.filter-bar-enter-from, .filter-bar-leave-to { opacity: 0; transform: translateY(-6px); }
.filter-group { display: flex; flex-direction: column; gap: 4px; min-width: 120px; flex: 1; }
.filter-label { font-size: 11px; font-weight: 600; color: var(--ink-muted, #888); text-transform: uppercase; letter-spacing: .04em; }
.filter-select,
.filter-input {
  padding: 7px 10px; border-radius: 7px;
  border: 1.5px solid rgba(31,30,26,.15);
  font-size: 13px; color: var(--ink, #2c2b27); background: var(--surface, #fff);
  outline: none;
  transition: border-color .14s;
  &:focus { border-color: var(--accent, #0078d4); }
}

/* ── Selection toolbar ── */
.selection-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  background: var(--system-navbar, #0f4c81);
  color: #fff;
  border-radius: 10px;
  padding: 10px 16px;
  margin-bottom: 4px;
}
.sel-count { font-size: 13px; font-weight: 600; opacity: .9; }
.sel-actions { display: flex; gap: 6px; flex-wrap: wrap; }

.sel-bar-enter-active, .sel-bar-leave-active { transition: all .2s ease; }
.sel-bar-enter-from, .sel-bar-leave-to { opacity: 0; transform: translateY(-6px); }

/* ── Export buttons ── */
.export-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 13px; border-radius: 7px; border: 1.5px solid transparent;
  font-size: 12px; font-weight: 600; cursor: pointer;
  transition: opacity .14s, background .14s; white-space: nowrap;
  &--doc    { background: rgba(255,255,255,.15); color: #fff; border-color: rgba(255,255,255,.3); &:hover { background: rgba(255,255,255,.25); } }
  &--pdf    { background: rgba(255,255,255,.08); color: #fff; border-color: rgba(255,255,255,.2); &:hover { background: rgba(255,255,255,.18); } }
  &--delete { background: rgba(239,68,68,.15); color: #fca5a5; border-color: rgba(239,68,68,.35); &:hover { background: rgba(239,68,68,.25); } }
  &--cancel { background: transparent; color: rgba(255,255,255,.7); border-color: rgba(255,255,255,.2); &:hover { background: rgba(255,255,255,.1); } }
}

/* ── Empty ── */
.empty-state {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 10px; padding: 80px 20px; text-align: center; color: var(--ink-muted, #aaa);
  svg { opacity: .3; }
}
.empty-title { margin: 0; font-size: 16px; font-weight: 700; color: var(--ink, #2c2b27); }
.empty-sub   { margin: 0; font-size: 13px; }

/* ── Doc list ── */
.doc-list { width: 100%; }

.doc-list-header {
  display: grid;
  grid-template-columns: 32px 3fr 100px 90px 1.2fr 120px;
  column-gap: 14px;
  padding: 8px 12px;
  font-size: 11px; font-weight: 700; color: var(--ink-muted, #888);
  text-transform: uppercase; letter-spacing: .05em;
  border-bottom: 1.5px solid rgba(31,30,26,.1);
  margin-bottom: 2px;
}

.doc-row {
  display: grid;
  grid-template-columns: 32px 3fr 100px 90px 1.2fr 120px;
  column-gap: 14px;
  align-items: center;
  padding: 10px 12px;
  border-radius: 8px;
  border-bottom: 1px solid rgba(31,30,26,.06);
  transition: background .1s;
  &:hover { background: rgba(31,30,26,.025); }
  &.selected { background: rgba(37,99,235,.05); border-color: rgba(37,99,235,.12); }
}

.col-check { display: flex; align-items: center; justify-content: center; }
.doc-checkbox { width: 15px; height: 15px; accent-color: var(--accent, #0078d4); cursor: pointer; }

.col-title { display: flex; align-items: center; gap: 10px; min-width: 0; }

.type-badge {
  flex-shrink: 0;
  font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 4px;
  background: #e0f2fe; color: #0369a1;
  white-space: nowrap; text-transform: uppercase; letter-spacing: .04em;
  &[data-cat="relatorio"] { background: #dbeafe; color: #1d4ed8; }
  &[data-cat="dialogo"]   { background: #dcfce7; color: #15803d; }
  &[data-cat="ocorrido"]  { background: #fef2f2; color: #dc2626; }
  &[data-cat="permissao"] { background: #f3e8ff; color: #7c3aed; }
}

.doc-title-wrap { min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.doc-title   { font-size: 13px; font-weight: 500; color: var(--ink, #2c2b27); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.doc-title-link {
  background: none; border: none; padding: 0; cursor: pointer; text-align: left;
  &:hover { color: var(--accent, #0078d4); text-decoration: underline; }
}
.doc-preview { font-size: 11px; color: var(--ink-muted, #aaa); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.col-date { font-size: 12px; color: var(--ink-muted, #888); white-space: nowrap; }

.status-pill {
  display: inline-block; font-size: 11px; font-weight: 600;
  padding: 2px 8px; border-radius: 20px; white-space: nowrap;
  &--enviado  { background: #dcfce7; color: #15803d; }
  &--rascunho { background: var(--surface-3, #edebe9); color: var(--ink-muted, #888); }
}

.col-author { display: flex; align-items: center; gap: 7px; min-width: 0; }
.author-avatar {
  width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;
  background: var(--accent, #0078d4); color: #fff;
  font-size: 11px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
}
.author-name { font-size: 12px; color: var(--ink-muted, #888); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.col-actions { display: flex; gap: 4px; justify-content: flex-end; }
.row-link {
  display: inline-flex; align-items: center; gap: 4px;
  background: transparent; border: none; cursor: pointer;
  font-size: 12px; font-weight: 500; color: var(--ink-muted, #666);
  padding: 5px 9px; border-radius: 6px;
  transition: background .1s, color .1s;
  &:hover { background: rgba(31,30,26,.06); color: var(--ink, #2c2b27); }
}

/* ── Language / confirm modals ── */
.lang-overlay {
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(0,0,0,.45);
  display: flex; align-items: center; justify-content: center; padding: 20px;
}
.lang-modal {
  background: var(--card, #fff);
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(0,0,0,.2);
  padding: 28px; width: 100%; max-width: 480px;
  display: flex; flex-direction: column; gap: 20px;
  &--sm { max-width: 360px; }
}
.lang-modal-header { text-align: center; }
.lang-modal-title { margin: 0 0 6px; font-size: 17px; font-weight: 700; color: var(--ink, #2c2b27); }
.lang-modal-sub   { margin: 0; font-size: 13px; color: var(--ink-muted, #888); }

.lang-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(120px, 1fr)); gap: 8px; }
.lang-option {
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  padding: 12px 8px; border-radius: 10px;
  border: 1.5px solid rgba(0,0,0,.1); background: var(--bg, #faf9f6);
  cursor: pointer; transition: border-color .14s, background .14s;
  &:hover { border-color: var(--accent, #0078d4); background: rgba(0,120,212,.05); }
}
.lang-flag  { font-size: 24px; line-height: 1; }
.lang-label { font-size: 11px; font-weight: 600; color: var(--ink, #2c2b27); }

.lang-modal-footer {
  display: flex; justify-content: center;
  &--gap { gap: 12px; }
}
.lang-cancel {
  padding: 8px 24px; border-radius: 7px; border: 1px solid rgba(0,0,0,.15);
  background: transparent; cursor: pointer;
  font-size: 13px; font-weight: 500; color: var(--ink-muted, #888);
  transition: background .12s;
  &:hover { background: rgba(0,0,0,.04); }
}
.delete-confirm-btn {
  padding: 8px 24px; border-radius: 7px; border: none;
  background: #dc2626; color: #fff; cursor: pointer;
  font-size: 13px; font-weight: 600;
  transition: background .12s;
  &:hover { background: #b91c1c; }
}

/* ── Responsive ── */
.documents-panel { display: flex; flex-direction: column; gap: 0; padding: 20px; }

.doc-date-mobile {
  display: none;
  font-size: 11px;
  color: var(--ink-muted, #aaa);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 700px) {
  .doc-list-header { display: none; }
  .doc-row {
    grid-template-columns: 32px 1fr auto;
    grid-template-rows: auto auto;
    gap: 6px 10px; padding: 12px;
  }
  .col-date, .col-status, .col-author { display: none; }
  .col-actions { grid-column: 3; grid-row: 1; }
  .col-title { grid-column: 2; grid-row: 1 / 3; }
  .doc-date-mobile { display: block; }
  .doc-preview { display: none; }
}
</style>
